import React from 'react';
import PropTypes from 'prop-types';
import { getApplicantText, normalizeApplication } from '../../utils/listingApplicationListUtil';
import { getApplicationStatus } from '../../pages/ApplicationsTabPage/ListingApplicationsTab/ListingApplications/ListingApplicationsApplicationSection/ListingApplicationsCardList/ListingApplicationCard/ListingApplicationCardUtil';
import { StatusHeader } from '../../pages/ApplicationsTabPage/ListingApplicationsTab/ListingApplications/ListingApplicationsApplicationSection/ListingApplicationsCardList/ListingApplicationCard/ListingApplicationCardStatusHeader/ListingApplicationCardStatusHeader';
import { Paragraph, Tag, Text, token } from '@zillow/constellation';
import { getTitleCase } from '../../utils/formatLogicUtil';
import { SectionNav } from '../SectionNav';
import UnstyledLink from '../UnstyledLink';
import { getLandlordReviewPath } from '../../constants/urls';
import { applicationStatusTypes } from '../../constants/statusConstants';
import { FULLSTORY_EXCLUDE } from '../../constants/fullstory';
import styled from 'styled-components';

const Wrapper = styled.div`
  border-bottom: 1px solid ${token('colors.borderLight')};

  &:first-of-type {
    border-top: 1px solid ${token('colors.borderLight')};
  }
`;

const ArchivedApplication = ({ application, propertyId }) => {
  const { statusType, applicationId } = application;
  const isClickable = statusType !== applicationStatusTypes.WITHDRAWN.id;
  const { type } = normalizeApplication(application);
  const statusInfo = getApplicationStatus({ application, type });
  const statusHeaderText = getApplicantText({ applicants: application.applicants });
  const applicantNames = application.applicants.map((applicant) => getTitleCase(applicant.name)).join(', ');

  const tag =
    statusType === applicationStatusTypes.WITHDRAWN.id || statusType === applicationStatusTypes.DECLINED.id ? (
      <Tag
        size="sm"
        marginBottom="xs"
        appearance={statusType === applicationStatusTypes.WITHDRAWN.id ? 'gray' : 'error'}
        data-test="archived-application__tag"
      >
        {application.statusType.toUpperCase()}
      </Tag>
    ) : null;

  const content = (
    <SectionNav withChevron={isClickable}>
      {tag}
      <StatusHeader
        type="body-tiny"
        fontColor="textMedium"
        fontType="finePrintHeading"
        data-test="archived-application__status-header"
      >
        {statusHeaderText}
      </StatusHeader>
      <Text
        type="h4"
        marginRight="xs"
        className={`ListingApplicationCard__Text ${FULLSTORY_EXCLUDE}`}
        data-test="archived-application__applicant-names"
      >
        {applicantNames}
      </Text>
      <Paragraph fontType="smallBody" fontColor="textMedium" data-test="archived-application__status-info">
        {statusInfo}
      </Paragraph>
    </SectionNav>
  );

  // withdrawn applications are not clickable but other archived applications are
  return (
    <Wrapper>
      {isClickable ? (
        <UnstyledLink
          id={`link-to-application-${applicationId}`}
          to={getLandlordReviewPath(propertyId, applicationId)}
          aria-label="View application"
          data-test="archived-application__link"
        >
          {content}
        </UnstyledLink>
      ) : (
        content
      )}
    </Wrapper>
  );
};

ArchivedApplication.propTypes = {
  propertyId: PropTypes.string.isRequired,
  application: PropTypes.object.isRequired,
};

export default ArchivedApplication;
